import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import useSWR from 'swr';
import { Combobox, Transition } from '@headlessui/react';
import { debounce } from 'lodash';
import { MdVerified } from 'react-icons/md';
import { BiChevronRight } from 'react-icons/bi';
import { RiHashtag } from 'react-icons/ri';
import type { TTQueryTypes } from '@shared-libs/tiktok';
import { classNames } from 'src/lib/utils';

const LoadingSpinner = () => (
  <svg className="absolute top-[12px] bottom-[15px] right-[15px] h-4 w-4 animate-spin text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
  </svg>
);

export interface TiktokAccount {
  user_id: string;
  username: string;
  full_name?: string;
  profile_pic_url?: string;
  is_verified?: boolean;
  is_private?: boolean;
}
export interface TiktokHashtag {
  target_id: string;
  name: string;
  mediaCount: number;
  search_result_subtitle: string;
}
type TiktokSearchResult = TiktokAccount | TiktokHashtag;

export default function TiktokSearchField({
  onResultSelected = (result: TiktokSearchResult) => { },
  onValueChange = (value: string) => { },
  onEnterKeyPressed = () => { },
  disabled = false,
  autocompleteOnSelect = true,
  searchType = 'users',
  autoFocus = true,
  disableSearch = false,
  defaultValue = '',
  ...props
} = {}) {

  const [results, setResults] = useState<TiktokSearchResult[]>([]);
  const [selectedResult, setSelectedResult] = useState<TiktokSearchResult | undefined>(undefined);
  const [inputValue, setInputValue] = useState(defaultValue);

  const compareResults = (a: TiktokSearchResult, b: TiktokSearchResult): boolean => {
    if (a && b) {
      if ('username' in a && 'username' in b)
        return a.username.toLowerCase() === b.username.toLowerCase();
      else if ('name' in a && 'name' in b)
        return a.name.toLowerCase() === b.name.toLowerCase();
    }
    return false;
  };
  async function fetchFromRapidAPI(query: string) {
    // console.log(`Selected result: ${'username' in selectedResult ? selectedResult.username : 'name' in selectedResult ? selectedResult.name : ''} | Entered query: ${query}`);
    console.log('selectedResult in fetchFromRapidAPI:', selectedResult);
    console.log('enteredQuery in fetchFromRapidAPI:', query);

    const targets = await fetch(`/api/tiktok/search-targets?type=${searchType as TTQueryTypes}&search=${query}`).then(res => res.json());
    console.log(`🔎 Fetched ${searchType} results for: ${query}`, targets);
    setResults(targets);
    // Update the cache manually after fetching
    return targets || [];
  }

  const inputRef = useRef(null);
  const { data, error } = useSWR(
    () => {
      return inputValue && inputValue.length >= 2 ? inputValue : null
    },
    disableSearch ? () => [] : fetchFromRapidAPI,
    {
      onSuccess: (data) => {
        if (Array.isArray(data)) {
          setResults(data); // Update the results variable with the fetched data
        }
      },
      onError: (error) => {
        console.error('error:', error);
      },
      errorRetryCount: 2,
    }
  );
  const isLoading = !error && !data && inputValue.length >= 2;

  useEffect(() => {
    if (inputRef.current && !inputRef.current.value && autoFocus)
      inputRef.current.focus(); // focus the input on first render
  }, [autoFocus]); // empty dependency array to run only on first render

  // Use useEffect to set the default value after mount
  useEffect(() => {
    if (inputRef.current && defaultValue) {
      inputRef.current.value = defaultValue;
      setInputValue(defaultValue);
    }
  }, [defaultValue]);

  const handleInputChange = debounce((event) => {
    const enteredQuery = event.target.value;
    setInputValue(enteredQuery); // This triggers the useSWR hook to fetch the data
  }, 1000);

  const filteredResults = results

  const handleResultSelect = (result: TiktokSearchResult) => {
    console.log("Selected result: ", result);
    setSelectedResult(result);
    onResultSelected(result);
    onValueChange('username' in result ? result.username : result.name);
    if (autocompleteOnSelect) {
      setInputValue('username' in result ? result.username : result.name);
      inputRef.current.value = 'username' in result ? result.username : result.name;
    } else {
      setInputValue('');
      inputRef.current.value = '';
    }
  };


  return (
    <React.Fragment>

      {/* {selectedResult && (
        // DEBUGING
        <div className="mb-4">
          <h3 className="text-lg font-medium">Current Selected {searchType === 'users' ? 'User' : 'Hashtag'}:</h3>
          <p className="text-sm text-gray-500">${'username' in selectedResult ? selectedResult.username : selectedResult.name}</p>
          <pre className="text-xs text-gray-500">
            {JSON.stringify(selectedResult, null, 2)}
          </pre>
          <pre className="text-xs text-gray-500">
            {JSON.stringify(results, null, 2)}
          </pre>
        </div>
      )} */}
      <Combobox
        value={selectedResult}
        by={compareResults}
        disabled={disabled || (props as { disabled?: boolean }).disabled}
      // onChange={handleResultSelect}
      >
        <div className="relative">
          {searchType === 'users' ? (
            <>
              {selectedResult && 'profile_pic_url' in selectedResult && inputValue === selectedResult.username ? (
                <Image unoptimized src={selectedResult.profile_pic_url ? selectedResult.profile_pic_url : '/img/default_avatar.jpeg'} alt={selectedResult.username}
                  width={20}
                  height={20}
                  className="absolute transform -translate-y-1/2 rounded-full left-4 top-1/2"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = '/img/default_avatar.jpeg'
                  }}
                  blurDataURL="/img/default_avatar.jpeg"
                />
              ) : (<></>
              )}
            </>
          ) : (
            // Hashtag
              // <MagnifyingGlassIcon className="absolute w-5 h-5 text-gray-500 transform -translate-y-1/2 left-5 top-1/2" />
              <></>
          )}


          <Combobox.Button as="div" className="flex">

            <Combobox.Input
              // type='search'
              ref={inputRef}
              // value={(props as { defaultValue?: string }).defaultValue ? (props as { defaultValue?: string }).defaultValue : ""}
              placeholder={`${searchType === 'users' ? '@selenagomez' : 'E.g: #fitness, #music, #business'}`}
              onBlur={(event) => {
                // Prevent input from clearing on blur
                event.preventDefault();
              }}
              onClick={(e: React.MouseEvent<HTMLInputElement>) => {
                if ((e.relatedTarget as Element)?.id?.includes('headlessui-combobox-button')) return;
                !open && (e.currentTarget.nextSibling as HTMLElement).click();
              }}
              onChange={(e) => {
                onValueChange(e.target.value);
                handleInputChange(e);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Tab') {
                  e.preventDefault();
                  // Dirty hack to move focus to the next focusable element
                  const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
                  const focusable = Array.from(document.querySelectorAll(focusableElements));
                  const currentIndex = focusable.indexOf(document.activeElement);
                  const nextIndex = (currentIndex + 1) % focusable.length;
                  const nextElement = focusable[nextIndex] as HTMLElement;
                  nextElement.focus();
                }
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (onEnterKeyPressed)
                    onEnterKeyPressed();
                }
                const regex = /^[a-zA-Z0-9_.]*$/; // Regular expression for allowed characters
                if (!regex.test(e.key)) { // If the entered key is not valid
                  e.preventDefault(); // Prevent the input value from being updated
                }
              }}
              onPaste={(e) => {
                e.preventDefault();
                const text = e.clipboardData.getData('text');
                const regex = /^[a-zA-Z0-9_.]*$/;
                // Remove characters that do not match the regex
                const filteredText = text.split('').filter(char => regex.test(char)).join('');
                console.log('Pasted text:', text, 'Filtered text:', filteredText);
                // Directly set the value of the input field
                if (e.target instanceof HTMLInputElement) {
                  e.target.value = filteredText;
                  // Trigger any necessary onChange handler or similar if needed
                  onValueChange(filteredText);
                }
              }}
              displayValue={(result: TiktokSearchResult) => 'username' in result ? result.username : result.name}
              className={classNames(`w-full h-10 text-sm border border-neutral-200 hover:border-neutral-300 focus:border-turquoise focus:shadow focus:ring-0 focus:shadow-turquoise-lighter focus:outline-none placeholder-neutral-500 rounded-lg px-3 ${disabled ? 'bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-100' : ''}`,
                (selectedResult && 'profile_pic_url' in selectedResult && inputValue === selectedResult.username) && '!pl-11'
              )}
              required={autoFocus}
              readOnly={disabled}
              // disabled={disabled || (props as { disabled?: boolean }).disabled}
              autoComplete={'off'}
              autoCorrect={'off'}
              autoCapitalize={'off'}
              spellCheck={false}
              maxLength={50}
              minLength={2}
              autoFocus={autoFocus}
              // tab-index="-1" aria-hidden="true"
              {...props}
            />
          </Combobox.Button>

          {isLoading && <LoadingSpinner />}
        </div>
        <Transition as="div"
          className="relative"
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100 relative z-10"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100 relative z-10"
          leaveTo="transform scale-95 opacity-0"
        >
          <Combobox.Options as="ul" className={`absolute left-0 top-[5px] max-h-[250px] overflow-scroll rounded-xl shadow-lg z-10 w-full bg-white ${filteredResults?.length ? 'border border-1 border-gray-200' : ''}`}>
            {(filteredResults && filteredResults.length > 0) && filteredResults?.map((result, index) => (
              <Combobox.Option as="li"
                key={'username' in result ? result.username : result.name}
                value={result}
                className={`rounded-lg flex items-center space-x-3 py-1.5 px-3 cursor-pointer hover:bg-gray-100 m-2 transition-all duration-100 ease-in-out`}
                onClick={() => handleResultSelect(result)}
              >
                {'username' in result && result.profile_pic_url ? (
                  <Image unoptimized src={result.profile_pic_url ? result.profile_pic_url : '/img/default_avatar.jpeg'} alt={result.username}
                    key={index}
                    width={20}
                    height={20}
                    className="w-10 h-10 rounded-full"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.onerror = null;
                      target.src = '/img/default_avatar.jpeg'
                    }}
                    blurDataURL="/img/default_avatar.jpeg"
                  />
                ) : <RiHashtag className="w-5 h-5 text-gray-500" />}

                <div className="flex items-center justify-between w-full">
                  <div>
                    <p className="flex items-center text-sm font-medium">
                      {'username' in result ? result.username : result.name}
                      {'is_verified' in result && result.is_verified && <MdVerified className="top-bar__title__verified-icon text-[#00a0ff] ml-1" />}
                    </p>
                    {'full_name' in result && <p className="text-sm text-gray-500">{result.full_name}</p>}
                  </div>
                  <BiChevronRight className="w-5 h-5 text-gray-500" />
                </div>
              </Combobox.Option>
            ))}
          </Combobox.Options>
        </Transition>
      </Combobox>
    </React.Fragment>
  );
}

